// ============================================================
// AttackerContext - attacker profiles, slot grid, fire controls,
// toggles, probability, and macros. Medium update frequency.
//
// State itself stays in DeathWatchSimulator's hooks; this context
// only distributes it (composition-root pattern).
// ============================================================
import { createContext, useContext } from 'react';

const AttackerContext = createContext(null);

export function AttackerProvider({ value, children }) {
  return <AttackerContext.Provider value={value}>{children}</AttackerContext.Provider>;
}

/**
 * Attacker state + handlers:
 *   atkProfiles, localCurrentProfile, showAtkMenu, setShowAtkMenu, newAtkName, setNewAtkName,
 *   handleLoadAtkProfile, handleSaveAtkProfile, handleNewAtkProfile, handleDeleteAtkProfile,
 *   currentSlots, selectedSlot, setSelectedSlot, editingSlot, setEditingSlot,
 *   slotClipboard, setSlotClipboard, dragSlot, setDragSlot,
 *   handleDuplicateSlot, handleCopySlot, handleCutSlot, handlePasteSlot, handleDeleteSlot, handleMoveSlot, handleMoveSlotBy,
 *   slot, isMelee, fireMode, setFireMode, fireModes, attackMod,
 *   attackToggles, setAttackToggles, attackCustomMods, setAttackCustomMods,
 *   hatred, setHatred, frenzied, setFrenzied, targetLocation, setTargetLocation,
 *   rangeBand, setRangeBand, freeAttack, setFreeAttack, rerollDamage, setRerollDamage,
 *   handleAttack, handleReload, attackerCharStats, attackerSourceCharacter,
 *   calledShotReduction, hasBerserkCharge, hasHammerBlow,
 *   probabilityResult, probabilityLoading, macros, onSaveMacro, onLoadMacro, onDeleteMacro
 */
/**
 * @returns {any} the attacker bucket.
 *
 * Untyped on purpose, and annotated so it stays usable from TypeScript:
 * `createContext(null)` plus the throw-guard below narrows the return to
 * `never`, so a .tsx consumer gets "Property 'x' does not exist on type
 * 'never'" for every field. ArmoryTab.tsx was the first to hit it.
 */
export function useAttacker() {
  const ctx = useContext(AttackerContext);
  if (!ctx) throw new Error('useAttacker must be used within an AttackerProvider');
  return ctx;
}
